import noop from 'lodash/noop';
import classNames from 'classnames';
import React from 'react';

import momentTZ from 'moment-timezone';
import gettext from '@eb/gettext';
import { isMediumDown } from '@eb/eds-utils';

/* Foundational Imports */
import { Dropdown } from '@eb/eds-containers';
import { ShareIosChunky } from '@eb/eds-iconography';
import { HeartFillChunky } from '@eb/eds-iconography';
import { HeartChunky } from '@eb/eds-iconography';

/* Atomical Imports */
import { Card } from '@eb/eds-card';
import { STYLE_BRAND, STYLE_DARK } from '@eb/eds-icon-button';

/* Molecular Imports */
import EventCardContent from './_internal/EventCardContent';
import {
    GRID_TYPE,
    STANDARD_STYLE,
    IMAGE_STYLE_FIXED,
} from './_internal/constants';
import { DateThumbnail } from '@eb/eds-date-thumbnail';
import { TYPE_SIMPLE, TYPE_COMPLEX } from '@eb/eds-modal';

/* Local Imports */
import { SaveModalContent } from './SaveEventModalContent';
import CollectionView from './_internal/CollectionView';
import { ShareModalContent } from './ShareEventModalContent';
import FormattedEventName from './FormattedEventName';

import { getEventTimeString, getFormattedPriceString } from './utils';

import {
    EVENTBRITE,
    EVENT_CARD_PROPTYPE,
    FREE_FLAG_TEXT,
    SOLD_OUT_FLAG_TEXT,
} from './constants';

import './eventCard.scss';

const getLocationNode = (locationInfo) => {
    let component;

    if (locationInfo) {
        component = (
            <div className="card-text--truncated__one">{locationInfo}</div>
        );
    }

    return component;
};

const isNotOnSale = (eventSalesStatus, repeatingInstanceCount) => {
    if (repeatingInstanceCount > 0) {
        return false;
    }

    if (eventSalesStatus) {
        return eventSalesStatus.salesStatus !== 'on_sale';
    }
};

export const getFlagInfo = (
    isFree,
    isSoldOut,
    eventSalesStatus,
    repeatingInstanceCount,
) => {
    let component;

    if (isFree || isSoldOut) {
        component = (
            <span>{isSoldOut ? SOLD_OUT_FLAG_TEXT : FREE_FLAG_TEXT}</span>
        );
    }

    if (eventSalesStatus) {
        if (isNotOnSale(eventSalesStatus, repeatingInstanceCount)) {
            component = (
                <span className="eds-text--center">
                    {eventSalesStatus.message}
                </span>
            );
        }
    }

    return component;
};

/*
This implementation of the EventCard utilizes data points and structure that
has been vetted and approved through multiple design iterations. This allows
us to keep a unified appearance of what data a user can expect to find on an
EventCard across our platform.

If you have special customizations or other things needed for your implementation
please feel free to roll your own version utilizing EventCardContent from EDS.
*/

export default class EventCard extends React.PureComponent {
    static propTypes = EVENT_CARD_PROPTYPE;

    static defaultProps = {
        style: STANDARD_STYLE,
        type: GRID_TYPE,
        imageStyle: IMAGE_STYLE_FIXED,
        isNoFollow: false,
        hideSaveButton: false,
        shouldShowIcons: true,
        isTargetBlank: false,
        isOnlineEvent: false,
    };

    state = {
        shouldRenderAddToCollectionDropdown: false,
        shouldRenderAddToCollectionModal: false,
        userTimezone: false,
    };

    componentDidMount() {
        this.setState({
            // Currently we only need to convert timezone on the browser, and it's okay
            // if we don't do so on the initial server render
            userTimezone: momentTZ.tz.guess(),
        });
    }

    componentDidUpdate(prevProps, prevState) {
        const { shouldRenderAddToCollectionModal } = this.state;
        const { eventCollections, hideOverlay } = this.props;

        const { eventCollections: prevEventCollections } = prevProps;
        const {
            shouldRenderAddToCollectionModal: prevShouldRenderAddToCollectionModal,
        } = prevState;

        if (shouldRenderAddToCollectionModal) {
            /* To ensure that the modal content is properly re-rendered
            when new collections added or state of the collection is changed
            we want re-trigger the render of the modal when it should be visible

            Additionally in order to prevent setState calls from render errors, we
            trigger initial render of the modal here rather than the render method itself. */
            if (
                !prevShouldRenderAddToCollectionModal ||
                eventCollections.length !== prevEventCollections.length
            ) {
                this._renderModalCollectionView();
            }
        } else if (
            !shouldRenderAddToCollectionModal &&
            prevShouldRenderAddToCollectionModal
        ) {
            hideOverlay();
        }
    }

    _onSaveClick = () => {
        const {
            id,
            onSaveEvent,
            savedByYou,
            isAuthenticated,
            userCollections,
        } = this.props;

        if (!isAuthenticated) {
            this._handleSaveModalShow();
        } else if (userCollections) {
            if (!savedByYou && onSaveEvent) {
                onSaveEvent(id, !savedByYou);
            }

            this._handleUserCollectionShow();
        } else if (onSaveEvent) {
            onSaveEvent(id, !savedByYou);
        }
    };

    _handleSaveModalShow() {
        const {
            showOverlay,
            id,
            onSaveEvent,
            name,
            startDate,
            imageUrl,
            isLazyImage,
        } = this.props;

        if (showOverlay) {
            showOverlay('modal', {
                type: TYPE_COMPLEX,
                children: (
                    <SaveModalContent
                        onClick={onSaveEvent}
                        id={id}
                        name={<FormattedEventName eventName={name} />}
                        imageUrl={imageUrl}
                        isLazyImage={isLazyImage}
                        startDate={startDate}
                    />
                ),
                onClose: noop,
            });
        }
    }

    _handleShareClick = () => {
        const {
            id: eventId,
            name: eventName,
            showOverlay,
            shareOptions,
            onWillShareClick,
            onDidShareClick,
        } = this.props;

        if (onWillShareClick) {
            onWillShareClick();
        }

        if (shareOptions && showOverlay) {
            const updatedShareOptions = {
                ...shareOptions,
                eventId,
                eventName,
                onClick: onDidShareClick,
            };

            showOverlay('modal', {
                type: TYPE_SIMPLE,
                title: gettext('Share with friends'),
                children: (
                    <ShareModalContent shareOptions={updatedShareOptions} />
                ),
                /* Due to a bug relating to safari, flexbox, and no set
                    height causing the modal to not appear, commenting out this
                    param until a fix is in place */
                // noMinHeight: true,
                onClose: noop,
            });
        }
    };

    _handleUserCollectionShow = () => {
        const { userCollections, onShowAddToCollectionView } = this.props;

        if (userCollections) {
            const isSmallScreen = isMediumDown();

            if (isSmallScreen) {
                this._handleRenderModalCollectionView();
            } else {
                this._renderDropdownCollectionView();
            }

            if (onShowAddToCollectionView) {
                onShowAddToCollectionView();
            }
        }
    };

    _renderDropdownCollectionView = () => {
        this.setState({ shouldRenderAddToCollectionDropdown: true });
    };

    _handleCloseDropdownCollectionView = () => {
        this.setState({ shouldRenderAddToCollectionDropdown: false });
    };

    _handleRenderModalCollectionView = () => {
        this.setState({ shouldRenderAddToCollectionModal: true });
    };

    _handleCloseModalCollectionView = () => {
        this.setState({ shouldRenderAddToCollectionModal: false });
    };

    _getCollectionViewProps = () => {
        const {
            id: eventId,
            userCollections,
            eventCollections,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            savedByYou,
            onSaveEvent,
            onShowCreateCollectionView,
        } = this.props;

        const isSmallScreen = isMediumDown();

        return {
            eventId,
            userCollections,
            eventCollections,
            onAddOrRemoveEventToCollection,
            onCreateCollection,
            savedByYou,
            onSaveEvent,
            isSmallScreen,
            onShowCreateCollectionView,
        };
    };

    _renderModalCollectionView = () => {
        const { showOverlay } = this.props;

        const content = (
            <div className="event-card__collection-modal-body">
                <CollectionView
                    {...this._getCollectionViewProps()}
                    onCloseDropdown={this._handleCloseModalCollectionView}
                />
            </div>
        );

        showOverlay('modal', {
            type: 'simple',
            children: content,
            onClose: this._handleCloseModalCollectionView,
        });
    };

    _getAddToCollectionDropdown = () => {
        const { shouldRenderAddToCollectionDropdown } = this.state;
        let component = null;

        if (shouldRenderAddToCollectionDropdown) {
            component = (
                <div className="eds-event-card__collection-dropdown-container">
                    <Dropdown
                        onClickOutside={this._handleCloseDropdownCollectionView}
                        align="right"
                    >
                        <CollectionView
                            {...this._getCollectionViewProps()}
                            onCloseDropdown={
                                this._handleCloseDropdownCollectionView
                            }
                        />
                    </Dropdown>
                </div>
            );
        }

        return component;
    };

    render() {
        const {
            edgeColor,
            name,
            startDate,
            startTime,
            ticketsBy,
            url,
            isFree,
            isSoldOut,
            savedByYou,
            style,
            type,
            imageStyle,
            containerSpacingClasses,
            excludeDateThumbnail,
            onClick,
            locationInfo,
            minPrice,
            formattedPriceString,
            shareOptions,
            repeatingInstanceCount,
            isNoFollow,
            isConsumer,
            focused,
            hideSaveButton,
            shouldShowIcons,
            imageUrl,
            isLazyImage,
            hidePriceDescription,
            isTargetBlank,
            timezone,
            isOnlineEvent,
            shouldConvertToUserTimezone,
            subtitle: subtitleProp,
            eventSalesStatus,
        } = this.props;
        let configProps = {};
        let formattedTimeAndDate = null;
        const { userTimezone } = this.state;

        /* Formatted event data strings and nodes to render as
        subContent on the eventCard */
        if (startDate) {
            formattedTimeAndDate = getEventTimeString(
                startDate,
                startTime,
                repeatingInstanceCount,
                isOnlineEvent,
                timezone,
                userTimezone,
                shouldConvertToUserTimezone,
            );

            /* If DateThumbnail should be rendered */
            if (!excludeDateThumbnail && !isConsumer) {
                configProps = {
                    ...configProps,
                    extraContent: <DateThumbnail date={startDate} />,
                };
            }
        }

        const formattedLocationNode = getLocationNode(locationInfo);

        /* Only render the share Icon if the shareOptions are passed through */
        if (shareOptions) {
            configProps = {
                ...configProps,
                secondaryIconType: <ShareIosChunky />,
                secondaryIconOnClick: this._handleShareClick,
                secondaryIconTitle: gettext(
                    'Share %(eventName)s with your friends.',
                    { eventName: name },
                ),
            };
        }

        let subtitle;
        let subContentOne = formattedTimeAndDate;
        let subContentTwo = formattedLocationNode;
        let subContentThree;

        if (!hidePriceDescription) {
            subContentThree = getFormattedPriceString(
                minPrice,
                isFree,
                ticketsBy,
            );
        }

        if (isConsumer) {
            subtitle = formattedTimeAndDate
                ? formattedTimeAndDate
                : subtitleProp;
            subContentOne = formattedLocationNode;
            subContentTwo = undefined;
            subContentThree = undefined;

            if (!hidePriceDescription) {
                subContentTwo = formattedPriceString
                    ? formattedPriceString
                    : getFormattedPriceString(minPrice, false, ticketsBy);
            }
        }

        let saveIconType = null;
        let saveIconStyle = null;
        let saveIconOnClick = null;
        let saveIconTitle = null;

        if (!hideSaveButton) {
            saveIconType = savedByYou ? <HeartFillChunky /> : <HeartChunky />;
            saveIconStyle = savedByYou ? STYLE_BRAND : STYLE_DARK;
            saveIconOnClick = this._onSaveClick;
            saveIconTitle = gettext('Save %(eventName)s to your collection.', {
                eventName: name,
            });
        }

        const collectionDropdown = this._getAddToCollectionDropdown();

        const eventCardClassName = classNames({
            'eds-event-card--consumer': isConsumer,
            /* Necessary to add 1 grid unit of padding
            to ensure hover effect occurs as expected */
            'eds-l-pad-all-1': !isConsumer,
        });

        /* Necessary to add 1 grid unit of padding
        to ensure hover effect occurs as expected */
        return (
            <div className={eventCardClassName}>
                <Card style="neutral">
                    <EventCardContent
                        {...configProps}
                        isConsumer={isConsumer}
                        style={style}
                        type={type}
                        imageStyle={imageStyle}
                        containerSpacingClasses={containerSpacingClasses}
                        isTargetBlank={
                            isTargetBlank || ticketsBy !== EVENTBRITE
                        }
                        isNoFollow={isNoFollow}
                        onClick={onClick}
                        focused={focused}
                        title={
                            <FormattedEventName
                                isConsumer={isConsumer}
                                eventName={name}
                            />
                        }
                        linkUrl={url}
                        flag={getFlagInfo(
                            isFree,
                            isSoldOut,
                            eventSalesStatus,
                            repeatingInstanceCount,
                        )}
                        flagDark={isNotOnSale(
                            eventSalesStatus,
                            repeatingInstanceCount,
                        )}
                        subtitle={subtitle}
                        subContentOne={subContentOne}
                        subContentTwo={subContentTwo}
                        subContentThree={subContentThree}
                        imageUrl={imageUrl}
                        isLazyImage={isLazyImage}
                        imageAlt={name}
                        placeholderBgColor={edgeColor}
                        shouldShowIcons={shouldShowIcons}
                        primaryIconType={saveIconType}
                        primaryIconStyle={saveIconStyle}
                        primaryIconOnClick={saveIconOnClick}
                        primaryIconTitle={saveIconTitle}
                        primaryIconDropdown={collectionDropdown}
                    />
                </Card>
            </div>
        );
    }
}
